import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Button,
  Select,
  MenuItem,
  FormControl,
  Tooltip
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import EmptyData from "components/EmptyData";
import apiClient from "../../Utils/apiClient";
import moment from "moment";

const cellSx = {
  padding: "16px 18px",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  fontSize: "13px",
  borderBottom: "1px solid #edf2f7",
  verticalAlign: "middle",
};

const navBtnSx = (disabled) => ({
    width: 32, height: 32, borderRadius: "8px",
    border: "1px solid #e0e0e0",
    backgroundColor: disabled ? "#fafafa" : "#fff",
    color: disabled ? "#ccc" : "#555",
    transition: "all 0.15s",
    "&:hover": !disabled ? { backgroundColor: "#f0f4ff", borderColor: "#c5d3f5", color: "#1976d2" } : {},
});

const PaginationBar = ({ currentPage, totalPages, totalCount, limit, onPageChange, onLimitChange }) => {
    const from = totalCount === 0 ? 0 : (currentPage - 1) * limit + 1;
    const to = Math.min(currentPage * limit, totalCount);

    const pageNumbers = useMemo(() => {
        if (totalPages <= 1) return [1];
        const delta = 1;
        const range = [];
        for (
            let i = Math.max(2, currentPage - delta);
            i <= Math.min(totalPages - 1, currentPage + delta);
            i++
        ) range.push(i);
        if (currentPage - delta > 2) range.unshift("...");
        if (currentPage + delta < totalPages - 1) range.push("...");
        range.unshift(1);
        if (totalPages > 1) range.push(totalPages);
        return range;
    }, [currentPage, totalPages]);

    return (
        <Box sx={{
            display: "flex", flexWrap: "wrap",
            alignItems: "center", justifyContent: "space-between",
            gap: 2, mt: "16px", pt: "14px",
            borderTop: "1px solid #f0f0f0", px: 1,
        }}>
            <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                <Typography sx={{ fontSize: "13px", color: "#666", whiteSpace: "nowrap" }}>
                    Showing{" "}
                    <Box component="span" sx={{ fontWeight: 700, color: "#333" }}>{from}–{to}</Box>
                    {" "}of{" "}
                    <Box component="span" sx={{ fontWeight: 700, color: "#333" }}>{totalCount}</Box>
                    {" "}records
                </Typography>
                <Box display="flex" alignItems="center" gap="6px">
                    <Typography sx={{ fontSize: "12px", color: "#999", whiteSpace: "nowrap" }}>
                        Rows per page:
                    </Typography>
                    <FormControl size="small" variant="outlined">
                        <Select
                            value={limit}
                            onChange={(e) => onLimitChange(Number(e.target.value))}
                            sx={{
                                fontSize: "12px", height: "30px", minWidth: "64px", borderRadius: "8px",
                                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e0e0e0" },
                                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#bbb" },
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#1976d2", borderWidth: "1px" },
                                "& .MuiSelect-select": { py: "4px", px: "10px" },
                            }}
                        >
                            {[5, 10, 20, 50].map((n) => (
                                <MenuItem key={n} value={n} sx={{ fontSize: "12px" }}>{n}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
            </Box>

            <Box display="flex" alignItems="center" gap="4px">
                <Tooltip title="First page" arrow>
                    <span>
                        <IconButton size="small" disabled={currentPage === 1} onClick={() => onPageChange(1)} sx={navBtnSx(currentPage === 1)}>
                            <FirstPageIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip title="Previous page" arrow>
                    <span>
                        <IconButton size="small" disabled={currentPage === 1} onClick={() => onPageChange(currentPage - 1)} sx={navBtnSx(currentPage === 1)}>
                            <ChevronLeftIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                    </span>
                </Tooltip>
                {pageNumbers.map((p, i) =>
                    p === "..." ? (
                        <Typography key={`dots-${i}`} sx={{ fontSize: "13px", color: "#ccc", px: "2px", lineHeight: "32px", userSelect: "none" }}>…</Typography>
                    ) : (
                        <Box
                            key={p}
                            onClick={() => p !== currentPage && onPageChange(p)}
                            sx={{
                                width: 32, height: 32,
                                display: "inline-flex", alignItems: "center", justifyContent: "center",
                                borderRadius: "8px", fontSize: "12px",
                                fontWeight: p === currentPage ? 700 : 500,
                                cursor: p === currentPage ? "default" : "pointer",
                                userSelect: "none", transition: "all 0.15s",
                                backgroundColor: p === currentPage ? "#1976d2" : "transparent",
                                color: p === currentPage ? "#fff" : "#555",
                                border: p === currentPage ? "1.5px solid #1976d2" : "1px solid transparent",
                                boxShadow: p === currentPage ? "0 2px 8px rgba(25,118,210,0.25)" : "none",
                                "&:hover": p !== currentPage ? { backgroundColor: "#f0f4ff", borderColor: "#c5d3f5", color: "#1976d2" } : {},
                            }}
                        >
                            {p}
                        </Box>
                    )
                )}
                <Tooltip title="Next page" arrow>
                    <span>
                        <IconButton size="small" disabled={currentPage === totalPages || totalPages === 0} onClick={() => onPageChange(currentPage + 1)} sx={navBtnSx(currentPage === totalPages || totalPages === 0)}>
                            <ChevronRightIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip title="Last page" arrow>
                    <span>
                        <IconButton size="small" disabled={currentPage === totalPages || totalPages === 0} onClick={() => onPageChange(totalPages)} sx={navBtnSx(currentPage === totalPages || totalPages === 0)}>
                            <LastPageIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                    </span>
                </Tooltip>
            </Box>
        </Box>
    );
};

const DriverTrips = () => {
  const { driverId } = useParams();
  const navigate = useNavigate();
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  const fetchTrips = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get(`/trip/driver/${driverId}?page=${page}&limit=${limit}`);
      
      const records = Array.isArray(response?.data?.data) ? response.data.data 
        : Array.isArray(response?.data) ? response.data
        : Array.isArray(response) ? response
        : Array.isArray(response?.trips) ? response.trips
        : Array.isArray(response?.result) ? response.result
        : [];
      
      setTrips(records);
      setTotalPages(response?.data?.totalPages || response?.totalPages || 1);
      setTotalCount(response?.data?.totalCount || response?.totalCount || records.length);
    } catch (err) {
      setError(err?.message || "Unable to load trips.");
      setTrips([]);
    } finally {
      setLoading(false);
    }
  }, [driverId, page, limit]);

  useEffect(() => {
    fetchTrips();
  }, [fetchTrips]);

  return (
    <DashboardLayout>
      <MDBox pt={6} pb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card sx={{ borderRadius: "12px", boxShadow: "0 4px 20px rgba(0,0,0,0.05)" }}>
              {/* Header */}
              <Box sx={{
                p: "20px 24px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                borderBottom: "1px solid #f0f0f0",
                backgroundColor: "#fafafa",
                borderTopLeftRadius: "12px",
                borderTopRightRadius: "12px",
              }}>
                <Box display="flex" alignItems="center" gap={2}>
                  <IconButton onClick={() => navigate(-1)} size="small" sx={{ color: "#555" }}>
                    <ArrowBackIcon />
                  </IconButton>
                  <MDTypography variant="h6" fontWeight="medium" color="text" sx={{ mb: 0 }}>
                    Driver Trips
                  </MDTypography>
                </Box>
              </Box>

              {/* Table */}
              <MDBox>
                {loading ? (
                  <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", p: 4 }}>
                    <Typography sx={{ fontSize: "14px", color: "#666" }}>Loading trips...</Typography>
                  </Box>
                ) : error ? (
                  <Box sx={{ p: 4, textAlign: "center" }}>
                    <Typography sx={{ fontSize: "14px", color: "#f44336" }}>{error}</Typography>
                    <Button variant="outlined" sx={{ mt: 2 }} onClick={fetchTrips}>Retry</Button>
                  </Box>
                ) : trips.length === 0 ? (
                  <Box sx={{ p: 4 }}><EmptyData message="No trips found for this driver." /></Box>
                ) : (
                  <>
                    <TableContainer sx={{ maxHeight: "calc(100vh - 280px)", overflowX: "auto" }}>
                      <Table stickyHeader sx={{ minWidth: 800 }}>
                        <MDBox component="thead" sx={{ backgroundColor: "#f8f9fa" }}>
                          <TableRow>
                            <TableCell sx={{ ...cellSx, fontWeight: 700, color: "#444" }}>S.No</TableCell>
                            <TableCell sx={{ ...cellSx, fontWeight: 700, color: "#444" }}>Loading Park</TableCell>
                            <TableCell sx={{ ...cellSx, fontWeight: 700, color: "#444" }}>Current Loc</TableCell>
                            <TableCell sx={{ ...cellSx, fontWeight: 700, color: "#444" }}>Destination</TableCell>
                            <TableCell sx={{ ...cellSx, fontWeight: 700, color: "#444" }}>State</TableCell>
                            <TableCell sx={{ ...cellSx, fontWeight: 700, color: "#444" }}>Date & Time</TableCell>
                            <TableCell sx={{ ...cellSx, fontWeight: 700, color: "#444" }}>Status</TableCell>
                          </TableRow>
                        </MDBox>
                        <TableBody>
                          {trips.map((trip, idx) => (
                            <TableRow key={trip._id} hover sx={{ "&:hover": { backgroundColor: "#f8fbff" } }}>
                              <TableCell sx={cellSx}>{(page - 1) * limit + idx + 1}</TableCell>
                              <TableCell sx={cellSx}>{trip.loadingPark || "N/A"}</TableCell>
                              <TableCell sx={cellSx}>
                                <Box sx={{ maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis" }}>
                                  {trip.curruntLocation || "N/A"}
                                </Box>
                              </TableCell>
                              <TableCell sx={cellSx}>
                                <Box sx={{ maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis" }}>
                                  {trip.destination || trip.finalDestination || "N/A"}
                                </Box>
                              </TableCell>
                              <TableCell sx={cellSx}>{trip.state || "N/A"}</TableCell>
                              <TableCell sx={cellSx}>
                                {trip.selectedDate ? moment(trip.selectedDate).format("DD MMM YYYY") : "N/A"}
                                {" "}
                                {trip.selectedTime ? trip.selectedTime : ""}
                              </TableCell>
                              <TableCell sx={cellSx}>
                                <Box sx={{
                                  display: "inline-flex", alignItems: "center", gap: "6px",
                                  px: "10px", py: "4px", borderRadius: "20px",
                                  backgroundColor: trip.status === "ACTIVE" ? "#E8F5FF" : trip.status === "COMPLETED" ? "#E8F9F0" : "#F5F5F5",
                                  color: trip.status === "ACTIVE" ? "#0057B8" : trip.status === "COMPLETED" ? "#1A7A45" : "#666",
                                  fontSize: "11px", fontWeight: 700, letterSpacing: "0.5px",
                                }}>
                                  {trip.status || "N/A"}
                                </Box>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    {totalPages > 0 && (
                      <PaginationBar
                        currentPage={page}
                        totalPages={totalPages}
                        totalCount={totalCount}
                        limit={limit}
                        onPageChange={(p) => setPage(p)}
                        onLimitChange={(l) => { setLimit(l); setPage(1); }}
                      />
                    )}
                  </>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
};

export default DriverTrips;
